'use client';

import { format, isValid, parse } from 'date-fns';
import { CalendarIcon, Clock } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '../../primitives/button';
import { Calendar } from '../../primitives/calendar';
import { Input } from '../../primitives/input';
import { Label } from '../../primitives/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover';
import { useLocalization } from '../../theme';
import { cn } from '../../utils/cn';

/**
 * Parse loosely-typed time text into hour/minute. Accepts "14:30", "1430",
 * "9", "2:5", and 12-hour forms with a meridiem ("2:30 pm", "230pm").
 * Returns null when it can't make a valid 24-hour time out of the input.
 */
const parseTimeText = (text: string): { h: number; m: number } | null => {
  const t = text.trim().toLowerCase();
  if (!t) return null;
  const pm = t.includes('p');
  const am = t.includes('a');
  const digits = t.replace(/[^0-9:]/g, '');
  if (!digits) return null;
  let h: number;
  let m: number;
  if (digits.includes(':')) {
    const [hh, mm = ''] = digits.split(':');
    h = Number(hh);
    m = mm === '' ? 0 : Number(mm);
  } else if (digits.length <= 2) {
    h = Number(digits);
    m = 0;
  } else {
    h = Number(digits.slice(0, digits.length - 2));
    m = Number(digits.slice(-2));
  }
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (pm && h < 12) h += 12;
  if (am && h === 12) h = 0;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { h, m };
};

/**
 * Mask typed digits into "HH:mm" as the operator types: digits only, capped
 * at 4, with a ":" auto-inserted after the first two. "1" -> "1", "14" ->
 * "14", "143" -> "14:3", "1430" -> "14:30".
 */
const maskTimeDigits = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

/**
 * A purely-numeric date format we can mask digit-by-digit (e.g. "dd/MM/yyyy",
 * "yyyy-MM-dd"). Text-month formats ("dd MMM yyyy") aren't maskable - they
 * stay free text. Excludes any time tokens.
 */
const isMaskableDateFormat = (fmt: string): boolean =>
  /^[dMy/.\- ]+$/.test(fmt) && !/M{3,}/.test(fmt);

type DateSeg = { type: 'd' | 'M' | 'y'; len: number; max: number };

/** Parse a numeric date format into ordered segments + separator. */
const dateFormatSegments = (fmt: string): { segs: DateSeg[]; sep: string } => {
  const sep = fmt.match(/[^dMy]/)?.[0] ?? '/';
  const segs: DateSeg[] = fmt.split(/[^dMy]+/).map((t) => {
    const type = t[0] as 'd' | 'M' | 'y';
    return { type, len: t.length, max: type === 'd' ? 31 : type === 'M' ? 12 : 9999 };
  });
  return { segs, sep };
};

/** Total digit count a numeric date format holds (e.g. dd/MM/yyyy -> 8). */
const dateFormatDigitCount = (fmt: string): number =>
  dateFormatSegments(fmt).segs.reduce((a, s) => a + s.len, 0);

/**
 * MUI-style segment-aware date mask. Validates each section as the operator
 * types so an invalid value can never be entered:
 *   - day:   first digit 4-9 -> "0X" and advance; 0-3 waits, second digit
 *            capped so the value is 01-31 (a digit that would exceed it is
 *            rejected).
 *   - month: first digit 2-9 -> "0X" and advance; 0-1 waits, capped 01-12.
 *   - year:  up to 4 digits.
 * Returns the formatted string (e.g. "04/06/2026"). Invalid keystrokes drop.
 */
const maskDateDigits = (raw: string, fmt: string, addTrailing = true): string => {
  const { segs, sep } = dateFormatSegments(fmt);
  const digits = raw.replace(/\D/g, '');
  const out: string[] = [];
  let i = 0;
  for (const seg of segs) {
    if (i >= digits.length) break;
    if (seg.len === 4) {
      const chunk = digits.slice(i, i + 4);
      out.push(chunk);
      i += chunk.length;
      if (chunk.length < 4) break;
      continue;
    }
    // 2-digit segment (day / month) with range validation.
    const maxTens = Math.floor(seg.max / 10); // day -> 3, month -> 1
    const c1 = digits[i] ?? '';
    const n1 = Number(c1);
    if (n1 > maxTens) {
      // Can only be a single-digit value: pad and advance to next section.
      out.push('0' + c1);
      i += 1;
      continue;
    }
    const c2 = digits[i + 1];
    if (c2 === undefined) {
      // Only one digit so far - still entering this section.
      out.push(c1);
      i += 1;
      break;
    }
    const two = c1 + c2;
    const val = Number(two);
    if (val >= 1 && val <= seg.max) {
      out.push(two);
      i += 2;
      continue;
    }
    // Second digit would make it invalid (00, 32-39, 13-19...) - reject it,
    // keep the first digit as a partial section and stop.
    out.push(c1);
    i += 1;
    break;
  }
  // If the last filled section is complete and another section follows, show a
  // trailing separator so the caret sits in the next section (MUI-style).
  const lastIdx = out.length - 1;
  const lastComplete = lastIdx >= 0 && (out[lastIdx]?.length ?? 0) === segs[lastIdx]?.len;
  const result = out.join(sep);
  return addTrailing && lastComplete && out.length < segs.length ? result + sep : result;
};

export interface UzDateTimePickerProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  value?: Date | null;
  defaultValue?: Date | null;
  onValueChange?: (value: Date | null) => void;
  disabled?: boolean;
  /**
   * `datetime` (default) shows the calendar + time row. `date` shows just the
   * calendar (no time row). `time` hides the calendar and renders only the
   * time controls. The parent always receives a `Date` (or null).
   */
  mode?: 'datetime' | 'date' | 'time';
  /**
   * `12` (default) shows the hour as 1-12 with an AM/PM toggle. `24` shows
   * the hour as 00-23 and hides the meridiem toggle.
   */
  hourFormat?: '12' | '24';
  /** Display format for the trigger label. Defaults depend on mode + hourFormat. */
  displayFormat?: string;
  /** Step in minutes for the hour/minute spinners. Default 5. */
  minuteStep?: number;
  /**
   * Earliest selectable date. When set the day-picker grays out
   * (and refuses) any day strictly before this. Pass `new Date()` to
   * lock past days while keeping month-forward navigation open.
   */
  minDate?: Date;
  /**
   * Latest selectable date. Same shape as `minDate`; days after this
   * are disabled. Optional.
   */
  maxDate?: Date;
  containerClassName?: string;
  triggerClassName?: string;
}

/**
 * Unified date + time picker - one trigger, one popover. Calendar selects
 * the date, hour/minute/meridiem selects render below, and the parent
 * receives a single `Date` (or null). Matches `UzDatePicker`'s trigger
 * styling so a form with both reads as a consistent set of fields.
 *
 * Pass `mode="time"` for a pure time picker (no calendar) and
 * `hourFormat="24"` for 24-hour time (00-23, no AM/PM).
 */
export const UzDateTimePicker = ({
  label,
  hint,
  error,
  required,
  placeholder,
  value,
  defaultValue,
  onValueChange,
  disabled,
  mode = 'datetime',
  hourFormat = '12',
  displayFormat,
  minuteStep = 5,
  minDate,
  maxDate,
  containerClassName,
  triggerClassName,
}: UzDateTimePickerProps): React.JSX.Element => {
  const localization = useLocalization();
  const isTimeOnly = mode === 'time';
  const isDateOnly = mode === 'date';
  // Date + time modes render as a typeable input + icon (MUI-style); datetime
  // keeps the single button trigger.
  const typeable = isTimeOnly || isDateOnly;
  const is24h = hourFormat === '24';
  const timeFmt = is24h ? 'HH:mm' : 'h:mm a';
  const triggerFmt =
    displayFormat ??
    (isTimeOnly
      ? timeFmt
      : isDateOnly
        ? localization.dateFormat
        : `${localization.dateFormat} · ${timeFmt}`);
  const resolvedPlaceholder =
    placeholder ??
    (isTimeOnly ? (is24h ? '14:00' : '02:00 PM') : isDateOnly ? 'Pick a date' : 'Pick date & time');

  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<Date | null>(defaultValue ?? null);
  const isControlled = value !== undefined;
  const selected = isControlled ? value : internal;
  // Which month the calendar should land on. Prefer the selected value; with no
  // value, land on the first SELECTABLE month rather than today - otherwise a
  // field whose `minDate` is in the future (e.g. a hotel check-in bounded by the
  // trip start / previous stay's check-out) opens on today with every day
  // greyed out, forcing the operator to page forward manually.
  const getLandingMonth = (): Date => {
    if (selected) return selected;
    const today = new Date();
    if (minDate && today.getTime() < minDate.getTime()) return minDate;
    if (maxDate && today.getTime() > maxDate.getTime()) return maxDate;
    return today;
  };
  // Fully-controlled displayed month. Previously we let react-day-picker
  // manage `firstMonth` itself via `defaultMonth`, but the chevrons
  // sometimes felt unresponsive (the click fired but the calendar
  // visually snapped back when the popover re-rendered). Owning the
  // displayed month locally and feeding RDP via `month` + `onMonthChange`
  // makes the prev/next buttons deterministic - every click flips this
  // state and re-renders.
  const [displayMonth, setDisplayMonth] = useState<Date>(() => getLandingMonth());

  // Typeable text shown in the date/time-mode input. Held locally so partial
  // typing isn't clobbered while the field is focused; it re-syncs from the
  // committed value on blur (see effect below).
  const [inputText, setInputText] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  const commit = (next: Date | null): void => {
    if (!isControlled) setInternal(next);
    onValueChange?.(next);
  };

  const handleDate = (date: Date | undefined): void => {
    if (!date) {
      commit(null);
      return;
    }
    // Preserve time-of-day when only the date changes.
    const base = selected ?? new Date();
    const next = new Date(date);
    next.setHours(base.getHours(), base.getMinutes(), 0, 0);
    commit(next);
    // Date-only: nothing more to set, so close on pick. Datetime: KEEP the
    // popover OPEN so the operator can adjust the time in the SAME session -
    // it used to close on date-pick, forcing a reopen just to change the
    // time (the reported pain). It still closes on outside-click / Escape.
    // Time-only never reaches here.
    if (isDateOnly) setOpen(false);
  };

  // Base date for time edits. When nothing's picked yet, start from a
  // zeroed time (00:00) so choosing an hour doesn't drag in the current
  // clock's minutes (and vice-versa). When a value exists, edit it in place.
  const timeBase = (): Date => {
    if (selected) return new Date(selected);
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // 24-hour: set the absolute hour directly. 12-hour: combine the 1-12
  // hour with the current meridiem. Minutes are preserved.
  const handleHour24 = (hour24: number): void => {
    const next = timeBase();
    next.setHours(hour24, next.getMinutes(), 0, 0);
    commit(next);
  };

  const handleHour = (hour12: number, meridiem: 'AM' | 'PM'): void => {
    handleHour24((hour12 % 12) + (meridiem === 'PM' ? 12 : 0));
  };

  const handleMinute = (minute: number): void => {
    const next = timeBase();
    next.setMinutes(minute, 0, 0);
    commit(next);
  };

  const handleMeridiem = (meridiem: 'AM' | 'PM'): void => {
    const base = selected ?? new Date();
    const current12 = base.getHours() % 12 === 0 ? 12 : base.getHours() % 12;
    handleHour(current12, meridiem);
  };

  // Parse loosely-typed date text. Tries the configured display format first,
  // then ISO, then the native Date parser. Returns null when unparseable.
  const parseDateText = (text: string): Date | null => {
    const t = text.trim();
    if (!t) return null;
    const ref = new Date();
    // Try the explicit display format first (e.g. "dd/MM/yyyy"), then the
    // app default, then ISO, then the native parser.
    const formats = [displayFormat, localization.dateFormat, 'dd/MM/yyyy', 'yyyy-MM-dd'].filter(
      (f): f is string => typeof f === 'string' && f.length > 0,
    );
    for (const f of formats) {
      const d = parse(t, f, ref);
      if (isValid(d)) return d;
    }
    const native = new Date(t);
    return isValid(native) ? native : null;
  };

  // Typeable input handler. Time mode masks digits into HH:mm; date mode
  // parses a free-typed date. Empty clears the value.
  const handleInput = (raw: string): void => {
    // Deleting? (new text shorter than what's shown). When deleting we must
    // NOT re-add the auto-advance trailing separator, or backspacing a "/"
    // would instantly bring it back and trap the caret.
    const deleting = raw.length < inputText.length;
    const text =
      isTimeOnly && is24h
        ? maskTimeDigits(raw)
        : isDateOnly && isMaskableDateFormat(triggerFmt)
          ? maskDateDigits(raw, triggerFmt, !deleting)
          : raw;
    setInputText(text);
    if (text.trim() === '') {
      commit(null);
      return;
    }
    if (isTimeOnly) {
      const parsed = parseTimeText(text);
      if (parsed) {
        const next = new Date(selected ?? new Date());
        next.setHours(parsed.h, parsed.m, 0, 0);
        commit(next);
      }
      return;
    }
    // Date mode. For a masked numeric format, only commit once every section
    // is filled - otherwise a partial like "04/06/20" could mis-parse.
    if (isDateOnly && isMaskableDateFormat(triggerFmt)) {
      if (text.replace(/\D/g, '').length < dateFormatDigitCount(triggerFmt)) return;
    }
    const d = parseDateText(text);
    if (d) {
      const next = new Date(d);
      // Keep any time-of-day already on the value (date-only ignores it).
      if (selected) next.setHours(selected.getHours(), selected.getMinutes(), 0, 0);
      commit(next);
      setDisplayMonth(next);
    }
  };

  // Footer quick-actions mirroring the reference design.
  const handleToday = (): void => {
    const next = new Date();
    // Preserve time-of-day if the operator already picked one.
    if (selected) next.setHours(selected.getHours(), selected.getMinutes(), 0, 0);
    commit(next);
    setDisplayMonth(next);
  };
  const handleClear = (): void => commit(null);
  // Reposition the calendar onto the selection when the popover
  // opens (or onto today if nothing's selected). Without this the
  // operator would re-open the picker after picking March and see
  // it still parked on whichever month they last navigated to.
  // Depend on the selected DATE (by value), not the Date object reference -
  // callers commonly pass a freshly-built `new Date(...)` each render, and
  // keying on the object would re-run this effect on every render, resetting
  // the calendar to the selected month and making the prev/next chevrons snap
  // back (you could never navigate to another month, e.g. into the past).
  const selectedDayKey = selected
    ? `${selected.getFullYear()}-${selected.getMonth()}-${selected.getDate()}`
    : '';
  useEffect(() => {
    if (!open) return;
    setDisplayMonth(getLandingMonth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDayKey]);

  // Keep the typeable input text in sync with the committed value, but never
  // while the operator is mid-edit (focused) - that would fight their typing.
  // On blur this re-normalises invalid/partial text to the value.
  // Show the value in the same format the trigger label uses (honours
  // displayFormat, e.g. "dd/MM/yyyy").
  const inputFmt = triggerFmt;
  useEffect(() => {
    if (!typeable || inputFocused) return;
    setInputText(selected ? format(selected, inputFmt) : '');
  }, [selected, inputFocused, typeable, inputFmt]);

  // Pre-compute time-control values (default to noon-ish when nothing picked yet).
  const display = selected ?? null;

  // Navigation bounds for the calendar. We expose a generous forward
  // window (+10y) and a moderate backward one (-2y) so the operator
  // can reach any realistic follow-up date via either the chevrons
  // OR the month/year dropdowns. Without explicit bounds the dropdown
  // captionLayout silently falls back to "today's year only", which
  // looks like the calendar refuses to navigate. Memoised so the
  // bounds don't churn each render.
  const { navStart, navEnd } = useMemo(() => {
    const t = new Date();
    return {
      navStart: new Date(t.getFullYear() - 2, 0, 1),
      navEnd: new Date(t.getFullYear() + 10, 11, 31),
    };
  }, []);
  const hour24Value = display ? display.getHours() : 0;
  const hour12 = display ? (display.getHours() % 12 === 0 ? 12 : display.getHours() % 12) : 12;
  const minute = display ? display.getMinutes() : 0;
  const meridiem: 'AM' | 'PM' = display && display.getHours() >= 12 ? 'PM' : 'AM';
  const hourOptions = is24h
    ? Array.from({ length: 24 }, (_, i) => i)
    : Array.from({ length: 12 }, (_, i) => i + 1);
  const minuteOptions = Array.from(
    { length: Math.ceil(60 / minuteStep) },
    (_, i) => i * minuteStep,
  );

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <Label className={cn(disabled && 'opacity-60')}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        {typeable ? (
          // Date / time modes: a real text input the operator can type into,
          // with an icon button that opens the popover (calendar or clock).
          <div className="relative">
            <Input
              value={inputText}
              onChange={(e) => handleInput(e.target.value)}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder={resolvedPlaceholder}
              disabled={disabled}
              aria-invalid={!!error || undefined}
              inputMode={isTimeOnly && is24h ? 'numeric' : 'text'}
              maxLength={isTimeOnly && is24h ? 5 : undefined}
              className={cn(
                'pr-9',
                error && 'border-destructive focus-visible:border-destructive',
                triggerClassName,
              )}
            />
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label={isTimeOnly ? 'Open time picker' : 'Open date picker'}
                className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isTimeOnly ? <Clock className="h-4 w-4" /> : <CalendarIcon className="h-4 w-4" />}
              </button>
            </PopoverTrigger>
          </div>
        ) : (
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                'w-full justify-start text-left font-normal',
                !display && 'text-muted-foreground',
                error && 'border-destructive focus-visible:ring-destructive',
                triggerClassName,
              )}
              aria-invalid={!!error || undefined}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {display ? format(display, triggerFmt) : resolvedPlaceholder}
            </Button>
          </PopoverTrigger>
        )}
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex flex-col">
            {!isTimeOnly && (
              <Calendar
                mode="single"
                selected={display ?? undefined}
                onSelect={handleDate}
                // Fully-controlled month: every prev/next chevron click
                // and every month/year dropdown change flows through
                // our `displayMonth` state. This is what fixed the
                // "chevrons don't navigate" bug.
                month={displayMonth}
                onMonthChange={setDisplayMonth}
                // Explicit navigation window: -2y back, +10y forward.
                // Without these the dropdown captionLayout (below)
                // silently caps at today's year. With them set, the
                // chevrons stay enabled across the entire range AND
                // the dropdowns populate the same range.
                startMonth={navStart}
                endMonth={navEnd}
                // Dropdown month + year selects in the caption row.
                // Gives the operator a direct jump even when the
                // chevron click feels slow (or when they want to land
                // on a specific month/year without 12 clicks).
                captionLayout="dropdown"
                // react-day-picker `disabled` matcher: array of bounds.
                // `{ before: minDate }` blocks days strictly before the
                // bound; navigation is independent and stays open.
                disabled={[
                  ...(minDate ? [{ before: minDate }] : []),
                  ...(maxDate ? [{ after: maxDate }] : []),
                ]}
              />
            )}
            {/* Time row - sits between the calendar and the footer.
                Hidden entirely in date-only mode. Hour / minute are native
                <select>s (see below) - a portaled Radix Select mis-positioned
                inside this popover. */}
            {!isDateOnly && (
              <div className="flex items-center justify-center gap-1.5 border-t px-3 py-2.5">
                {/* Hour / minute use NATIVE <select> - a Radix Select
                    (portaled) mis-positioned its dropdown here (rendered at the
                    viewport bottom, behind the form, and vanished at some zoom
                    levels) because it's nested inside the calendar popover.
                    The native control is browser-anchored, so it's reliable at
                    every zoom and never escapes the popover. */}
                <select
                  aria-label="Hour"
                  value={is24h ? hour24Value : hour12}
                  onChange={(e) =>
                    is24h
                      ? handleHour24(Number(e.target.value))
                      : handleHour(Number(e.target.value), meridiem)
                  }
                  className="focus:border-ring h-8 cursor-pointer rounded-md border border-zinc-200 bg-white px-2 text-sm font-medium outline-none hover:bg-zinc-50"
                >
                  {hourOptions.map((h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span className="text-zinc-500">:</span>
                <select
                  aria-label="Minute"
                  value={minute - (minute % minuteStep)}
                  onChange={(e) => handleMinute(Number(e.target.value))}
                  className="focus:border-ring h-8 cursor-pointer rounded-md border border-zinc-200 bg-white px-2 text-sm font-medium outline-none hover:bg-zinc-50"
                >
                  {minuteOptions.map((m) => (
                    <option key={m} value={m}>
                      {String(m).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                {!is24h && (
                  <div className="ml-1 inline-flex overflow-hidden rounded-md border border-zinc-200">
                    {(['AM', 'PM'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => handleMeridiem(m)}
                        className={cn(
                          'px-2 py-1 text-xs font-medium',
                          meridiem === m
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-white text-zinc-600 hover:bg-zinc-50',
                        )}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Footer quick-actions: Today (left), then Clear + Done (right).
                Done explicitly closes the popover so the operator confirms
                the date + time in one go, instead of the popover auto-closing
                on date-pick (which forced a reopen to set the time). */}
            <div className="flex items-center justify-between border-t bg-zinc-50/60 px-4 py-2">
              {isTimeOnly ? (
                <span />
              ) : (
                <button
                  type="button"
                  onClick={handleToday}
                  className="text-foreground hover:text-primary text-sm font-medium hover:underline"
                >
                  Today
                </button>
              )}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-muted-foreground hover:text-foreground text-sm font-medium hover:underline"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-3 py-1 text-sm font-medium"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {error ? (
        <p className="text-destructive text-xs font-medium">{error}</p>
      ) : hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
};
